import { LegalPage } from "./LegalPage.jsx";

const DEMO_HTML = `
<p>
  Building or buying a new home is one of the largest financial decisions
  most Australians ever make. Use this checklist as a starting point — not
  a substitute for advice from your solicitor, conveyancer, or independent
  building inspector.
</p>

<h2>Before you sign anything</h2>
<ul>
  <li><strong>Title and zoning.</strong> Confirm the property's zoning, overlays, and any easements with a current title search.</li>
  <li><strong>Soil and contour reports.</strong> A Class P or Class H site classification can add tens of thousands to the build cost. Get the report before signing.</li>
  <li><strong>Council planning controls.</strong> Heritage overlays, neighbourhood character provisions, and bushfire attack levels all change what you can build.</li>
  <li><strong>Easement load.</strong> Sewer, drainage, and electrical easements can rule out certain footprints. Bring the title to your designer before locking in a plan.</li>
  <li><strong>Service connections.</strong> Confirm the cost and lead time for power, water, sewer, and NBN. These are often quoted separately and can extend the timeline.</li>
</ul>

<h2>Reading the contract</h2>
<ul>
  <li><strong>Inclusions list.</strong> Cross-check every line of the inclusions schedule against the quote. Vague entries (e.g. "appliance package") should be itemised.</li>
  <li><strong>Provisional sums.</strong> Provisional sums are estimates the builder may exceed at cost. Push for fixed pricing wherever possible.</li>
  <li><strong>Variations.</strong> Read the variation clause carefully — it sets out how changes are priced and how disputes are resolved.</li>
  <li><strong>Progress payments.</strong> Confirm the schedule (deposit, base, frame, lock-up, fixing, completion) matches your finance approval terms.</li>
  <li><strong>Liquidated damages.</strong> If your build is delayed, what's the daily rate the builder pays you? A standard rate is around $300/day after the agreed completion date.</li>
</ul>

<h2>Independent advice</h2>
<p>
  We strongly recommend you engage an <strong>independent building
  inspector</strong> at four key stages:
</p>
<ol>
  <li>Pre-slab — to verify the site set-out and reinforcement.</li>
  <li>Frame stage — to verify wall plates, trusses, and bracing.</li>
  <li>Pre-plaster — to verify rough-ins (electrical, plumbing, mechanical).</li>
  <li>Practical completion — to compile the defects list before handover.</li>
</ol>
<p>
  Inspections cost a few hundred dollars each and routinely catch issues
  worth ten times that. A reputable builder will welcome them.
</p>

<h2>Finance and insurance</h2>
<ul>
  <li>Confirm your loan covers progress payments — not all construction loans do.</li>
  <li>Verify the builder holds <strong>Domestic Building Insurance</strong> (DBI) for projects over $16,000 in Victoria.</li>
  <li>Check whether contents insurance covers items stored on site during construction.</li>
  <li>Plan for council rates, water rates, and owners-corporation fees during the build period.</li>
</ul>

<h2>After handover</h2>
<ul>
  <li>Use your <strong>maintenance period</strong> (90 days) to log any defects in writing.</li>
  <li>Keep all warranty documentation and serial numbers in one folder.</li>
  <li>Schedule the 6-month and 12-month aftercare visits with your builder before handover day.</li>
</ul>

<p><em>This checklist is provided for informational purposes only. It is not legal or financial advice. Engage your own legal and financial advisors before signing any contract.</em></p>
`;

export default function DueDiligencePage() {
  return (
    <LegalPage
      field="dueDiligence"
      title="Due Diligence Checklist"
      description="A checklist of things to consider before signing a building contract."
      demoHtml={DEMO_HTML}
    />
  );
}
